import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClienteAuth } from "@/contexts/ClienteAuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, Plus, HardDrive, FileText, Wrench, Building2, ChevronRight, Loader2, X, Download, Users, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { logoColor } from "@/lib/logos";

const EQUIPO_VACIO = { tipo_equipo: "", nombre: "", marca: "", modelo: "", numero_serie: "",
  codigo_interno: "", numero_box: "", fecha_adquisicion: "", proveedor: "", notas: "" };

export default function ClienteDashboard() {
  const navigate = useNavigate();
  const { api, cliente, logout, refresh } = useClienteAuth();
  const [stats, setStats] = useState({});
  const [equipos, setEquipos] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [nuevo, setNuevo] = useState(null); // objeto equipo en creación o null
  const [clinicaEdit, setClinicaEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mants, setMants] = useState([]);
  const [ubis, setUbis] = useState([]);
  const [tiposBox, setTiposBox] = useState([]);
  const [ubiEdit, setUbiEdit] = useState(null); // ubicación en edición/creación

  const load = () => {
    api.get("/api/portal/dashboard").then((r) => setStats(r.data)).catch(() => {});
    api.get("/api/portal/equipos").then((r) => setEquipos(r.data)).catch(() => {});
    api.get("/api/portal/solicitudes").then((r) => setSolicitudes(r.data)).catch(() => {});
    api.get("/api/portal/mantenciones").then((r) => setMants(r.data)).catch(() => {});
    api.get("/api/portal/ubicaciones").then((r) => { setUbis(r.data.ubicaciones || []); setTiposBox(r.data.tipos_box || []); }).catch(() => {});
  };

  const guardarUbicacion = async () => {
    if (!ubiEdit.direccion?.trim()) return toast.error("Ingresa la dirección de la sede");
    setBusy(true);
    try {
      if (ubiEdit.id) await api.put(`/api/portal/ubicaciones/${ubiEdit.id}`, ubiEdit);
      else await api.post("/api/portal/ubicaciones", ubiEdit);
      toast.success("Ubicación guardada");
      setUbiEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const crearEquipo = async () => {
    if (!nuevo.tipo_equipo) return toast.error("Indica el tipo de equipo");
    setBusy(true);
    try {
      const r = await api.post("/api/portal/equipos", nuevo);
      toast.success("Equipo agregado");
      setNuevo(null); load();
      navigate(`/cliente/equipos/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const guardarClinica = async () => {
    setBusy(true);
    try {
      await api.put("/api/portal/clinica", clinicaEdit);
      toast.success("Datos de la clínica guardados");
      setClinicaEdit(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const cl = cliente?.clinica || {};

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img src={logoColor} alt="Odontofix" className="h-8" />
            <span className="text-sm font-semibold text-[#384091] truncate">Mi Clínica</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-800">{cliente?.nombre}</p>
              <p className="text-xs text-gray-500">{cl.razon_social || cliente?.email}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/cliente"); }}>
              <LogOut className="h-4 w-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Hola, {cliente?.nombre?.split(" ")[0]}</h1>
          <p className="text-gray-500 text-sm">El repositorio de tu clínica, siempre listo para una inspección.</p>
          {stats.org && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#384091]/5 border border-[#384091]/15 px-3 py-1 text-xs text-[#384091]">
              <Users className="h-3.5 w-3.5" />
              <b>{stats.org.razon_social || "Tu clínica"}</b> · RUT {stats.org.rut || "—"} · {stats.org.usuarios} usuario(s)
            </div>
          )}
        </div>

        {/* Stats (clickeables) */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Equipos", value: stats.equipos || 0, icon: HardDrive, color: "#0FB4B7", go: "#equipos" },
            { label: "Documentos", value: stats.documentos || 0, icon: FileText, color: "#384091", go: "/cliente/documentos" },
            { label: "Solicitudes", value: stats.solicitudes || 0, icon: Wrench, color: "#534AB7", go: "#solicitudes" },
          ].map((s) => (
            <Card key={s.label} className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => s.go.startsWith("/") ? navigate(s.go) : document.querySelector(s.go)?.scrollIntoView({ behavior: "smooth" })}>
              <CardContent className="p-4 text-center">
                <s.icon className="h-5 w-5 mx-auto mb-1" style={{ color: s.color }} />
                <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Solicitar por WhatsApp (directo) */}
        <a href={`https://wa.me/56929894282?text=${encodeURIComponent(`Hola Odontofix, soy ${cliente?.nombre || ""} de ${cl.razon_social || "mi clínica"} (RUT ${cl.rut || "-"}). Necesito solicitar una mantención.`)}`}
          target="_blank" rel="noopener noreferrer" className="block">
          <Button className="w-full h-12 bg-[#25D366] hover:bg-[#1fb757] text-white font-semibold">
            <MessageCircle className="h-5 w-5 mr-2" /> Solicitar mantención por WhatsApp
          </Button>
        </a>

        {/* Mi clínica */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[#384091]" /> Datos de la clínica
              </h2>
              {!clinicaEdit && (
                <Button variant="outline" size="sm" onClick={() => setClinicaEdit({ ...cl })}>Editar</Button>
              )}
            </div>
            {!clinicaEdit ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <p><span className="text-gray-400">Razón social:</span> {cl.razon_social || "—"}</p>
                <p><span className="text-gray-400">RUT:</span> {cl.rut || "—"}</p>
                <p><span className="text-gray-400">Dirección:</span> {cl.direccion || "—"}{cl.comuna ? `, ${cl.comuna}` : ""}</p>
                <p><span className="text-gray-400">Teléfono:</span> {cl.telefono || "—"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[["razon_social", "Razón social"], ["rut", "RUT"], ["direccion", "Dirección"],
                    ["comuna", "Comuna"], ["region", "Región"], ["telefono", "Teléfono"]].map(([k, l]) => (
                    <div key={k}><Label className="text-xs">{l}</Label>
                      <Input value={clinicaEdit[k] || ""} onChange={(e) => setClinicaEdit((p) => ({ ...p, [k]: e.target.value }))}
                        className="h-10 bg-white" /></div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-[#384091] text-white" onClick={guardarClinica} disabled={busy}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setClinicaEdit(null)}>Cancelar</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mis ubicaciones (sedes y boxes) */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#534AB7]" /> Mis ubicaciones
            </h2>
            <Button size="sm" variant="outline" onClick={() => setUbiEdit({ nombre: "", direccion: "", comuna: "", region: "", boxes: [] })}>
              <Plus className="h-4 w-4 mr-1" /> Agregar sede
            </Button>
          </div>
          <p className="text-xs text-gray-400 -mt-2 mb-3">
            Una razón social puede tener varias direcciones. Crea cada sede con sus boxes y luego asígnalos a tus equipos.
          </p>

          {ubiEdit && (
            <Card className="border-0 shadow-md mb-3 ring-2 ring-[#534AB7]/30">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#534AB7]">{ubiEdit.id ? "Editar sede" : "Nueva sede"}</h3>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setUbiEdit(null)}><X className="h-4 w-4" /></Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Nombre de la sede</Label>
                    <Input value={ubiEdit.nombre || ""} onChange={(e) => setUbiEdit((p) => ({ ...p, nombre: e.target.value }))}
                      placeholder="Ej: SEDE PROVIDENCIA" className="h-10 bg-white" /></div>
                  <div><Label className="text-xs">Dirección *</Label>
                    <Input value={ubiEdit.direccion || ""} onChange={(e) => setUbiEdit((p) => ({ ...p, direccion: e.target.value }))}
                      className="h-10 bg-white" /></div>
                  <div><Label className="text-xs">Comuna</Label>
                    <Input value={ubiEdit.comuna || ""} onChange={(e) => setUbiEdit((p) => ({ ...p, comuna: e.target.value }))}
                      className="h-10 bg-white" /></div>
                  <div><Label className="text-xs">Región</Label>
                    <Input value={ubiEdit.region || ""} onChange={(e) => setUbiEdit((p) => ({ ...p, region: e.target.value }))}
                      className="h-10 bg-white" /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold">Boxes de esta sede</Label>
                    <Button size="sm" variant="outline" className="h-8"
                      onClick={() => setUbiEdit((p) => ({ ...p, boxes: [...(p.boxes || []), { nombre: `BOX ${(p.boxes || []).length + 1}`, tipo: "Atención general", m2: "" }] }))}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Box
                    </Button>
                  </div>
                  {(ubiEdit.boxes || []).map((b, i) => (
                    <div key={i} className="grid grid-cols-[1fr,1fr,80px,auto] gap-2 mb-2">
                      <Input value={b.nombre} placeholder="Nombre (Box 1)" className="h-9 bg-white"
                        onChange={(e) => setUbiEdit((p) => ({ ...p, boxes: p.boxes.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x) }))} />
                      <Select value={b.tipo} onValueChange={(v) => setUbiEdit((p) => ({ ...p, boxes: p.boxes.map((x, j) => j === i ? { ...x, tipo: v } : x) }))}>
                        <SelectTrigger className="h-9 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {tiposBox.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input value={b.m2} placeholder="m²" inputMode="decimal" className="h-9 bg-white no-uppercase"
                        onChange={(e) => setUbiEdit((p) => ({ ...p, boxes: p.boxes.map((x, j) => j === i ? { ...x, m2: e.target.value } : x) }))} />
                      <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-400"
                        onClick={() => setUbiEdit((p) => ({ ...p, boxes: p.boxes.filter((_, j) => j !== i) }))}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button className="w-full h-11 bg-[#534AB7] hover:bg-[#443C9E] text-white" onClick={guardarUbicacion} disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar sede"}
                </Button>
              </CardContent>
            </Card>
          )}

          {ubis.length === 0 && !ubiEdit ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 text-center text-sm text-gray-400">
                Crea tu primera sede (dirección) y sus boxes para ordenar tus equipos.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {ubis.map((u) => (
                <Card key={u.id} className="border-0 shadow-sm">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#534AB7]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-[#534AB7]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{u.nombre}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {u.direccion}{u.comuna ? `, ${u.comuna}` : ""} · {(u.boxes || []).length} box(es)
                        {(u.boxes || []).length > 0 ? `: ${(u.boxes || []).map((b) => b.nombre).join(", ")}` : ""}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setUbiEdit(JSON.parse(JSON.stringify(u)))}>Editar</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Equipos */}
        <div id="equipos">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-800">Mis equipos</h2>
            <Button size="sm" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white"
              onClick={() => setNuevo({ ...EQUIPO_VACIO })}>
              <Plus className="h-4 w-4 mr-1" /> Agregar equipo
            </Button>
          </div>

          {nuevo && (
            <Card className="border-0 shadow-md mb-3 ring-2 ring-[#0FB4B7]/30">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#0FB4B7]">Nuevo equipo</h3>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setNuevo(null)}><X className="h-4 w-4" /></Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Tipo de equipo *</Label>
                    <Select value={nuevo.tipo_equipo} onValueChange={(v) => setNuevo((p) => ({ ...p, tipo_equipo: v }))}>
                      <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {(stats.tipos_equipo || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Sede (dirección)</Label>
                    <Select value={nuevo.ubicacion_id || ""} onValueChange={(v) => {
                      const u = ubis.find((x) => x.id === v);
                      setNuevo((p) => ({ ...p, ubicacion_id: v, box_id: "", ubicacion_nombre: u?.nombre || "", ubicacion_direccion: u?.direccion || "" }));
                    }}>
                      <SelectTrigger className="h-11 bg-white"><SelectValue placeholder={ubis.length ? "Seleccionar sede" : "Crea una sede primero"} /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {ubis.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre} — {u.direccion}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Box</Label>
                    <Select value={nuevo.box_id || ""} onValueChange={(v) => {
                      const u = ubis.find((x) => x.id === nuevo.ubicacion_id);
                      const b = (u?.boxes || []).find((x) => x.id === v);
                      setNuevo((p) => ({ ...p, box_id: v, numero_box: b ? b.nombre : p.numero_box }));
                    }}>
                      <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Seleccionar box" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {(ubis.find((x) => x.id === nuevo.ubicacion_id)?.boxes || []).map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.nombre} · {b.tipo}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select></div>
                  {[["marca", "Marca"], ["modelo", "Modelo"], ["numero_serie", "N° de serie"],
                    ["proveedor", "Proveedor"]].map(([k, l]) => (
                    <div key={k}><Label className="text-xs">{l}</Label>
                      <Input value={nuevo[k]} onChange={(e) => setNuevo((p) => ({ ...p, [k]: e.target.value }))}
                        className="h-11 bg-white" /></div>
                  ))}
                  <div><Label className="text-xs">Fecha de adquisición</Label>
                    <Input type="date" value={nuevo.fecha_adquisicion}
                      onChange={(e) => setNuevo((p) => ({ ...p, fecha_adquisicion: e.target.value }))}
                      className="h-11 bg-white no-uppercase" /></div>
                </div>
                <Button className="w-full h-11 bg-[#0FB4B7] hover:bg-[#0D999C] text-white" onClick={crearEquipo} disabled={busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar equipo"}
                </Button>
              </CardContent>
            </Card>
          )}

          {equipos.length === 0 && !nuevo ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-8 text-center text-gray-400">
                Aún no registras equipos. Agrega tu primer sillón, autoclave o rayos X.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {equipos.map((e) => (
                <Link key={e.id} to={`/cliente/equipos/${e.id}`}>
                  <Card className="border-0 shadow-sm hover:shadow-md transition-shadow mb-2">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#0FB4B7]/10 flex items-center justify-center flex-shrink-0">
                        <HardDrive className="h-5 w-5 text-[#0FB4B7]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {e.tipo_equipo}{e.marca ? ` · ${e.marca}` : ""}{e.modelo ? ` ${e.modelo}` : ""}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {e.numero_box ? `${e.numero_box} · ` : ""}{(e.documentos || []).length} documento(s)
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300" />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Mis Documentos (módulo propio: búsqueda, filtros y traslados) */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate("/cliente/documentos")}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#384091]/10 flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-[#384091]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800">Mis documentos</p>
              <p className="text-xs text-gray-500 truncate">
                {stats.documentos || 0} documento(s) — busca por palabra, filtra por tipo y traslada entre equipos
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </CardContent>
        </Card>

        {/* Historial Odontofix (mantenciones migradas por el admin / RUT) */}
        {mants.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 space-y-3">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#0FB4B7]" /> Historial Odontofix
                <span className="text-xs font-normal text-gray-400">mantenciones realizadas por nuestro equipo</span>
              </h2>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {mants.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {m.folio} — {m.tipo === "preventiva" ? "Mant. preventiva" : "Mant. correctiva"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{m.fecha} · {m.sucursal || ""} {m.numero_box ? `· ${m.numero_box}` : ""}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                      onClick={async () => {
                        try {
                          const r = await api.get(`/api/portal/mantenciones/${m.id}/pdf`, { responseType: "blob" });
                          const url = URL.createObjectURL(r.data); window.open(url, "_blank");
                        } catch { toast.error("PDF no disponible"); }
                      }}>
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Solicitudes */}
        {solicitudes.length > 0 && (
          <div id="solicitudes">
            <h2 className="font-semibold text-gray-800 mb-3">Mis solicitudes</h2>
            <div className="space-y-2">
              {solicitudes.slice(0, 8).map((s) => (
                <Card key={s.id} className="border-0 shadow-sm">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {s.folio} — Mantención {s.tipo}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{s.equipo || "Equipo no especificado"}</p>
                    </div>
                    <Badge className={s.estado === "abierta" ? "bg-blue-50 text-blue-700 border-0" : "bg-emerald-50 text-emerald-700 border-0"}>
                      {s.estado}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
